import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { isAdminRoute } from "../../utils/authHelpers";
import {
  FaThreads,
  FaTiktok,
  FaXTwitter,
  FaYoutube,
  FaEnvelope,
  FaUser,
} from "react-icons/fa6";

import {
  FaHome,
  FaUserCircle,
  FaSignOutAlt,
  FaCog,
  FaFileAlt,
  FaInfoCircle,
  FaSignInAlt,
} from "react-icons/fa";

import hamburgerLight from "../../images/hamburger-light.svg";
import hamburgerDark from "../../images/hamburger-dark.svg";
import darkLogo from "../../images/logo-black.png";
import ig from "../../images/uim_instagram.svg";
import fb from "../../images/uim_facebook.svg";
import close from "../../images/close.svg";
import { UserRole } from "../../types";

const SideMenu = ({ mode, userRole }: { mode: string; userRole: UserRole }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { logout } = useAuth();

  const { t } = useTranslation();

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = async () => {
    toggleMenu();
    logout();
  };
  const isAdmin = userRole === "admin" || userRole === "super_admin";

  return (
    <>
      <img
        src={mode === "dark" ? hamburgerLight : hamburgerDark}
        alt=""
        onClick={toggleMenu}
        className={`h-4 cursor-pointer ${menuOpen ? "hidden" : "block"}`}
      />

      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
          onClick={toggleMenu}
        />
      )}

      {/* Sliding Side Menu */}
      <div
        className={`flex flex-col justify-between gap-y-4 fixed right-0 top-0 h-full w-[300px] xs:w-[400px] p-10 pt-[102px] bg-white text-black transform transition-transform duration-700 z-50 ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <button
          className="w-[30px] h-[30px] flex justify-center items-center absolute top-10 right-10 focus:outline-none"
          onClick={toggleMenu}
        >
          <img src={close} alt="close" />
        </button>

        <nav className="flex flex-col gap-3 text-gray-7 text-base leading-[20px] xs:text-[18px] xs:leading-[22px] font-[900]">
          {userRole === "user" && (
            <>
              <Link
                onClick={toggleMenu}
                to="/dashboard"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaHome className="text-gray-7" />
                {t("home")}
              </Link>
              <Link
                onClick={toggleMenu}
                to="/submit"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaFileAlt className="text-gray-7" />
                Submit report
              </Link>
              <Link
                onClick={toggleMenu}
                to="/profile"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaUserCircle className="text-gray-7" />
                Profile
              </Link>
              <Link
                onClick={toggleMenu}
                to="/profile/settings"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaCog className="text-gray-7" />
                Settings
              </Link>
              <button
                onClick={handleLogout}
                className="text-white font-extrabold text-lg leading-[22px] bg-[#1D1B1B] mt-5 w-[197px] flex justify-center items-center gap-2 rounded-[200px] py-[15px] px-[43.5px]"
              >
                <FaSignOutAlt />
                Log Out
              </button>
            </>
          )}

          {isAdmin && (
            <>
              <Link
                onClick={toggleMenu}
                to="/admin/dashboard"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaHome className="text-gray-7" />
                {t("home")}
              </Link>
              <button
                onClick={handleLogout}
                className="text-white font-extrabold text-lg leading-[22px] bg-[#1D1B1B] mt-5 w-[197px] flex justify-center items-center gap-2 rounded-[200px] py-[15px] px-[43.5px]"
              >
                <FaSignOutAlt />
                Log Out
              </button>
            </>
          )}

          {!userRole && (
            <>
              <a
                onClick={toggleMenu}
                href="/#home"
                className="px-6 py-[14px] flex items-center gap-3"
              >
                <FaHome className="text-gray-7" />
                {t("home")}
              </a>
              {!isAdminRoute() && (
                <>
                  <Link
                    onClick={toggleMenu}
                    to="/#about"
                    className="px-6 py-[14px] flex items-center gap-3"
                  >
                    <FaInfoCircle className="text-gray-7" />
                    {t("about")}
                  </Link>
                  <Link
                    onClick={toggleMenu}
                    to="/submit"
                    className="px-6 py-[14px] flex items-center gap-3"
                  >
                    <FaFileAlt className="text-gray-7" />
                    {t("submitTip")}
                  </Link>
                  <Link
                    onClick={toggleMenu}
                    to="/#contact"
                    className="px-6 py-[14px] flex items-center gap-3"
                  >
                    <FaEnvelope className="text-gray-7" />
                    {t("contactUs")}
                  </Link>
                </>
              )}
              {isAdminRoute() && (
                <Link
                  onClick={toggleMenu}
                  to="/login"
                  className="px-6 py-[14px] flex items-center gap-3"
                >
                  <FaUser className="text-gray-7" />
                  User Login
                </Link>
              )}
              <Link
                onClick={toggleMenu}
                to={isAdminRoute() ? "/admin/login" : "/login"}
                className="text-white bg-[#1D1B1B] mt-5 w-[197px] flex justify-center items-center gap-3 rounded-[200px] py-[21px]"
              >
                <FaSignInAlt className="text-white" />
                {t("login")}
              </Link>
            </>
          )}
        </nav>

        <div className="flex flex-col gap-5">
          <div className="socials flex items-center gap-3">
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.facebook.com/profile.php?id=61570321112245"
            >
              <img src={fb} alt="facebook" className="h-6" />
            </a>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.instagram.com/unspokenncontact/"
            >
              <img src={ig} alt="instagram" className="h-6" />
            </a>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.tiktok.com/@unspokenncontact?lang=en"
            >
              <FaTiktok size={24} />
            </a>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://x.com/unspokenhelp"
            >
              <FaXTwitter size={24} />
            </a>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.threads.net/@unspokenncontact"
            >
              <FaThreads size={24} />
            </a>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://youtube.com/@unspokenncontact?si=NnUShwdS_xY_YdqF"
            >
              <FaYoutube size={24} />
            </a>
          </div>
          <Link to="/">
            <img className="w-[117px]" src={darkLogo} alt="unspokenn" />
          </Link>
          <p className="copyright max-w-[207px] text-gray-7 text-[10px] font-[400]">
            {t("copyrightText")}
          </p>
        </div>
      </div>
    </>
  );
};

export default SideMenu;
